import axios from 'axios';
import { isTokenExpiredOrExpiringSoon } from './tokenUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api/v1';

// Refresh the access token using the stored refresh token.
// Stores the new tokens in localStorage and returns the new access token,
// or throws if the refresh request fails.
//
// Deduped via a shared in-flight promise: refresh tokens are single-use
// (rotated + revoked server-side on each use), so if several concurrent
// requests each triggered their own refresh with the same token, the first
// would revoke it and the rest would fail with a now-dead token and log the
// user out. Sharing one in-flight refresh means concurrent callers all wait
// on a single rotation and receive the same new token.
let inFlightRefresh = null;

export async function refreshAccessToken() {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const response = await axios.post(`${API_URL}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    return access_token;
  })();

  try {
    return await inFlightRefresh;
  } finally {
    inFlightRefresh = null;
  }
}

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests — refresh proactively if the stored token is expired
// or within REFRESH_BUFFER_SECONDS of expiry, so the upload flow never sends
// a request with a token the server will reject as expired.
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('accessToken');

    if (token && isTokenExpiredOrExpiringSoon(token)) {
      try {
        token = await refreshAccessToken();
      } catch {
        // Refresh failed — clear auth state and redirect so the user can
        // log in again. Reject the pending request to surface the error.
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh for cases where the proactive check above was not
// triggered (e.g. clock skew, token invalidated server-side). The reactive
// retry is a safety net — normal requests should succeed via the interceptor
// above once the proactive refresh is in place.
//
// Special case: 401 with login_required:true means the user is not logged in
// at all (not an expired token — there is no token). Redirect them to /login
// with a return path so they can come back after signing in. This must be
// checked BEFORE the refresh attempt so we do not try to refresh a
// non-existent token for a genuinely logged-out user.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // login_required: true → user is logged out, not just token-expired.
    // Redirect to /login with current page as return destination.
    if (
      error.response?.status === 401 &&
      error.response?.data?.login_required === true
    ) {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
      return Promise.reject(error);
    }

    // Plain 401 (no login_required flag) — may be an expired token.
    // Attempt a single reactive refresh (existing PR #69 logic).
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  googleLogin: (idToken) => api.post('/auth/google', { id_token: idToken }),
  logout: (refreshToken) => api.delete('/auth/logout', { data: { refresh_token: refreshToken } }),
};

// Videos API
export const videosAPI = {
  getAll: (params) => api.get('/videos', { params }),
  getById: (id) => api.get(`/videos/${id}`),
  getFeatured: () => api.get('/videos/featured'),
  getTrending: () => api.get('/videos/trending'),
  getRecent: () => api.get('/videos/recent'),
  recordView: (id) => api.post(`/videos/${id}/view`),
  create: (data) => api.post('/videos', data),
  update: (id, data) => api.put(`/videos/${id}`, data),
  delete: (id) => api.delete(`/videos/${id}`),
  thumbnailPreview: (id, time) => api.get(`/videos/${id}/thumbnail_preview`, { params: { time } }),
};

// Artists API
export const artistsAPI = {
  getAll: () => api.get('/artists'),
  getById: (id) => api.get(`/artists/${id}`),
  delete: (id) => api.delete(`/artists/${id}`),
};

// Purchases API (read-only — purchases are created via verified Stripe webhook)
export const purchasesAPI = {
  getAll: () => api.get('/purchases'),
  check: (videoId) => api.get(`/purchases/check/${videoId}`),
};

// Stripe API
export const stripeAPI = {
  createCheckout: (videoId) => api.post('/stripe/checkout', { video_id: videoId }),
};

// Users API
export const usersAPI = {
  getCurrent: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getPublicProfile: (id) => api.get(`/users/${id}/profile`),
  getAvatar: (id) => `${API_URL}/users/${id}/avatar`,
  updateProfile: (data) => api.patch('/profile', data),
};

// Admin YouTube Import API
export const youtubeAdminAPI = {
  getVideos: (params) => api.get('/admin/youtube/videos', { params }),
  searchArtists: (query) => api.get('/admin/youtube/artists/search', { params: { query } }),
  importVideos: (videos) => api.post('/admin/youtube/import', { videos }),
  parseVideos: (videos) => api.post('/admin/youtube/parse', { videos }),
};

// Admin Videos API (artist management + featuring)
export const adminVideosAPI = {
  reassignArtist: (videoId, params) => api.patch(`/admin/videos/${videoId}/reassign_artist`, params),
  mergeArtists: (videoId, params) => api.post(`/admin/videos/${videoId}/merge_artists`, params),
  feature: (id) => api.patch(`/admin/videos/${id}/feature`),
  unfeature: (id) => api.patch(`/admin/videos/${id}/unfeature`),
};

// Admin Artists API (standalone create + CRUD)
export const adminArtistsAPI = {
  getAll: () => api.get('/admin/artists'),
  create: (name) => api.post('/admin/artists', { name }),
  destroy: (id) => api.delete(`/admin/artists/${id}`),
};

// Admin Users API
export const adminUsersAPI = {
  getAll: (params) => api.get('/admin/users', { params }),
  getById: (id) => api.get(`/admin/users/${id}`),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  changeRole: (id, role) => api.patch(`/admin/users/${id}/change_role`, { role }),
  delete: (id) => api.delete(`/admin/users/${id}`),
  promoteToArtist: (id, mergeArtistId) =>
    api.post(`/admin/users/${id}/promote_to_artist`,
      mergeArtistId ? { merge_artist_id: mergeArtistId } : {}),
  search: (params) => api.get('/admin/users', { params }),
  uploadAvatar: (id, dataUrl) => api.post(`/admin/users/${id}/avatar`, { avatar: dataUrl }),
};

// Admin Analytics API
export const adminAnalyticsAPI = {
  get: () => api.get('/admin/analytics'),
  // Sales analytics. `signal` (AbortController) lets the UI cancel stale range fetches.
  sales: (params, { signal } = {}) => api.get('/admin/analytics/sales', { params, signal }),
  videoSales: (id, params, { signal } = {}) =>
    api.get(`/admin/analytics/videos/${id}/sales`, { params, signal }),
};

// Admin Stripe API (backfill one Stripe Product + Price per priced video)
export const adminStripeAPI = {
  syncProducts: () => api.post('/admin/stripe/sync_products'),
  // Refund backfill: POST queues the job; GET reports the last finished run (no enqueue).
  syncRefunds: () => api.post('/admin/stripe/sync_refunds'),
  syncRefundsStatus: () => api.get('/admin/stripe/sync_refunds'),
};

// Admin Purchases API (support view — sortable, searchable, paginated)
export const adminPurchasesAPI = {
  list: (params) => api.get('/admin/purchases', { params }),
};

// Comments API
export const commentsAPI = {
  // Get top-level comments for a video
  getVideoComments: (videoId, params) => api.get(`/videos/${videoId}/comments`, { params }),
  // Get a single comment
  getById: (id) => api.get(`/comments/${id}`),
  // Get replies for a comment
  getReplies: (commentId, params) => api.get(`/comments/${commentId}/replies`, { params }),
  // Create a top-level comment
  create: (videoId, content) => api.post(`/videos/${videoId}/comments`, { comment: { content } }),
  // Create a reply to a comment
  createReply: (commentId, content) => api.post(`/comments/${commentId}/replies`, { comment: { content } }),
  // Update a comment
  update: (id, content) => api.patch(`/comments/${id}`, { comment: { content } }),
  // Delete a comment
  delete: (id) => api.delete(`/comments/${id}`),
};

// Likes API
export const likesAPI = {
  // Like a video
  likeVideo: (videoId) => api.post(`/videos/${videoId}/like`),
  // Unlike a video
  unlikeVideo: (videoId) => api.delete(`/videos/${videoId}/like`),
  // Like a comment
  likeComment: (commentId) => api.post(`/comments/${commentId}/like`),
  // Unlike a comment
  unlikeComment: (commentId) => api.delete(`/comments/${commentId}/like`),
};

// Battle Votes API
export const votesAPI = {
  getVotes: (videoId) => api.get(`/videos/${videoId}/votes`),
  // Vote for a SIDE (team). 2v2-ready; a 1v1 battle is just two single-artist sides.
  castVote: (videoId, side) => api.post(`/videos/${videoId}/vote`, { side }),
};

// Rankings API
export const rankingsAPI = {
  getAll: (params) => api.get('/rankings', { params }),
};

// Mux Video API
export const muxAPI = {
  getPlaybackToken: (videoId) => api.get(`/videos/${videoId}/playback_token`),
};

// Mux Admin API
export const muxAdminAPI = {
  createUpload: (params) => api.post('/admin/mux/uploads', params),
  createLiveStream: (params) => api.post('/admin/mux/live_streams', params),
  createVideo: (params) => api.post('/admin/mux/videos', params),
};

export default api;